// Pure helpers for the Change Bookings standalone app (MYS-7069)

// Maps PEAK rebook error/warning codes to translation keys (see the PEAK Bulk API error spec).
// Covers both per-item operation codes (rebook / changePersonalization / cancelOldReservation)
// and top-level HTTP error codes. Resolve with reasonKeyForCode(); unmapped codes fall back to
// PEAK's raw message, then a generic key.
export const REBOOK_ERROR_KEYS = {
	// per-item operation codes
	NO_AVAILABILITY: 'rebookErrorNoAvailability',
	NOT_A_MYSERVICE_EVENT: 'rebookErrorNotMysEvent',
	ITEM_CANCELLED: 'rebookErrorItemCancelled',
	ITEM_NOT_REBOOKABLE: 'rebookErrorNotRebookable',
	DATE_IN_PAST: 'rebookErrorDateInPast',
	OUTSIDE_BOOKING_WINDOW: 'rebookErrorOutsideBookingWindow',
	INTERNAL_ERROR: 'rebookErrorInternal',
	PERSONALIZATION_NOT_EXISTS: 'rebookErrorPersonalizationNotExists',
	INVALID_ATTRIBUTE: 'rebookErrorInvalidAttribute',
	CANCELLATION_FAILED: 'rebookErrorCancellationFailed',
	PRICE_CHANGED: 'rebookErrorPriceChanged',
	// top-level HTTP error codes
	malformed_parameter: 'rebookHttpMalformedParameter',
	authentication_failed: 'rebookHttpAuthenticationFailed',
	not_found: 'rebookHttpNotFound',
	precondition_failed: 'rebookHttpPreconditionFailed',
	general_error: 'rebookHttpGeneralError',
}

export function reasonKeyForCode(code) {
	return REBOOK_ERROR_KEYS[code] ?? null
}

// Remaining places formula — MUST stay identical to the Service Designer contingent
// calendar (see components/serviceDesigner/contingentMonitor/ContingentSlot.vue) so both
// views show the same numbers for the same slot.
export function slotUsed(slot) {
	return slot.used - slot.overbookFree
}

export function slotFree(slot) {
	return slot.total - slotUsed(slot)
}

export function dayHasFreeSlot(day) {
	return day.slots?.some(slot => slotFree(slot) > 0)
}

export function deepClone(value) {
	return value === undefined ? undefined : JSON.parse(JSON.stringify(value))
}

export function deepEqual(a, b) {
	return JSON.stringify(a) === JSON.stringify(b)
}

export function isValidEmail(value) {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value ?? '')
}

export function formatPrice(value, currency) {
	if (value === null || value === undefined) return ''
	return `${currency ?? ''} ${Number(value).toFixed(2)}`.trim()
}

// 'YYYY-MM-DD' (+ optional 'HH:mm') -> 'DD.MM.YYYY' (+ ', HH:mm')
export function formatDateTime(date, time) {
	if (!date) return ''
	const [y, m, d] = date.split('-')
	const formatted = `${d}.${m}.${y}`
	return time && time !== '00:00' ? `${formatted}, ${time}` : formatted
}

// builds the dateTime string sent to the backend / PEAK
export function buildDateTime(date, time) {
	return time && time !== '00:00' ? `${date} ${time}` : date
}

// initial editable snapshot of an order item
export function buildDraft(item) {
	return {
		date: item.consumptionDate ?? null,
		time: item.consumptionTime ?? null,
		firstName: item.personalization?.firstName ?? '',
		lastName: item.personalization?.lastName ?? '',
		email: item.personalization?.email ?? '',
		additional: { ...(item.personalization?.additional ?? {}) },
	}
}

// Waits for the background rebooking job (MYS-7377). Never reports "failed" while the job may
// still commit at PEAK:
//   { outcome: 'done', result }   — job finished; result is the usual submit result
//   { outcome: 'unknown', error } — job ended in ERROR (timeout, connection reset, pod restart) or
//                                   can no longer be found: PEAK may or may not have committed
//   { outcome: 'timeout' }        — still running at the cap; the job keeps running server-side
// A failed poll (network blip, 502/504 during a rollout) does not end the wait. Real link errors
// (token expired/invalid) are thrown so the page can switch to the invalid-link view.
export async function pollRebookingJob({
	getStatus,
	isLinkError,
	intervalMs,
	capMs,
	onPollError = () => {},
	sleep = ms => new Promise(resolve => setTimeout(resolve, ms)),
	now = () => Date.now(),
}) {
	const startedAt = now()
	while (now() - startedAt < capMs) {
		await sleep(intervalMs)
		let status
		try {
			status = await getStatus()
		}
		catch (error) {
			// job id unknown or not this order's — not an expired link
			if (error?.message === 'errorRebookingJobNotFound') return { outcome: 'unknown', error }
			if (isLinkError(error)) throw error
			onPollError(error)
			continue
		}
		if (status.status === 'DONE') return { outcome: 'done', result: status.result }
		// structured PEAK rejections come back as DONE with requestError; ERROR means the outcome is unknown
		if (status.status === 'ERROR') return { outcome: 'unknown', error: status.error }
	}
	return { outcome: 'timeout' }
}
